import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Users, FlaskConical, Dna, Settings, LogOut, Moon, Sun } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

interface SidebarProps {
  currentView: string;
  isMobileMenuOpen: boolean;
  session: any;
  onNavClick: (view: any) => void;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, isMobileMenuOpen, session, onNavClick, onLogout }) => {
  
  const [userRole, setUserRole] = useState<string>('');
  const [isDark, setIsDark] = useState(false);

  // Extraction des infos utilisateur de base
  const user = session?.user;
  const email = user?.email || '';
  const fullName = user?.user_metadata?.full_name || email;
  const initial = (fullName?.[0] || email?.[0] || 'U').toUpperCase();

  // Initialisation du thème
  useEffect(() => {
    // Vérifier si un thème est sauvegardé ou utiliser la préférence système
    const isDarkMode = localStorage.getItem('theme') === 'dark' || 
      (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    setIsDark(isDarkMode);
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Récupération du rôle depuis la table profiles ou les métadonnées
  useEffect(() => {
    const fetchRole = async () => {
        if (isSupabaseConfigured() && user) {
            const { data } = await supabase.from('profiles').select('role').eq('id', user.id).single();
            if (data) {
                setUserRole(data.role);
            } else {
                setUserRole('technician'); // Défaut
            }
        } else {
            // Mode Démo / Mock
            setUserRole(user?.role || 'admin');
        }
    };
    fetchRole();
  }, [user]);

  const toggleTheme = () => {
    const newMode = !isDark;
    setIsDark(newMode);
    
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const getRoleLabel = (role: string) => {
      switch(role) {
          case 'admin': return 'Administrateur';
          case 'biologist': return 'Biologiste';
          case 'technician': return 'Technicien';
          case 'viewer': return 'Observateur';
          default: return 'Utilisateur';
      }
  };

  const handleLogoutWithLog = async () => {
      if (isSupabaseConfigured() && user) {
          await supabase.from('audit_logs').insert({
              user_id: user.id,
              action: 'LOGOUT',
              entity: 'AUTH'
          });
      }
      onLogout();
  };

  return (
    <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform duration-300 ease-in-out
        md:static md:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 flex items-center gap-3 text-white border-b border-slate-800">
           <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center font-bold shrink-0">L</div>
           <span className="font-bold text-lg tracking-wide truncate">SIL Mycobactérie</span>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          <NavItem 
            icon={LayoutDashboard} 
            label="Tableau de bord" 
            active={currentView === 'dashboard'} 
            onClick={() => onNavClick('dashboard')} 
          />
          <div className="pt-4 pb-2 px-3 text-xs font-bold uppercase text-slate-500 tracking-wider">
            Opérations
          </div>
          <NavItem 
            icon={Users} 
            label="Patients / Individus" 
            active={currentView === 'patients'} 
            onClick={() => onNavClick('patients')} 
          />
          <NavItem 
            icon={FlaskConical} 
            label="Prélèvements" 
            active={currentView === 'samples'} 
            onClick={() => onNavClick('samples')} 
          />
          <NavItem 
            icon={Dna} 
            label="Microbiologie" 
            active={currentView === 'microbio'} 
            onClick={() => onNavClick('microbio')} 
          />
          
          <div className="pt-4 pb-2 px-3 text-xs font-bold uppercase text-slate-500 tracking-wider">
            Système
          </div>
          <NavItem 
            icon={Settings} 
            label="Administration" 
            active={currentView === 'admin'} 
            onClick={() => onNavClick('admin')} 
          />
        </nav>

        <div className="p-4 border-t border-slate-800 bg-slate-900 space-y-4">
          
          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
            <span>{isDark ? 'Mode Clair' : 'Mode Sombre'}</span>
          </button>

          <div className="flex items-center gap-3 px-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 border ${userRole === 'admin' ? 'bg-purple-600 border-purple-400' : 'bg-slate-700 border-slate-600'}`}>
              {initial}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-white truncate" title={email}>{fullName}</p>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5 uppercase tracking-wide font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block shadow-[0_0_5px_rgba(34,197,94,0.6)]"></span>
                {getRoleLabel(userRole)}
              </p>
            </div>
          </div>
          <button 
            onClick={handleLogoutWithLog}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
          >
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>
      </aside>
  );
};

const NavItem = ({ icon: Icon, label, active, onClick }: any) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      active 
      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/20' 
      : 'text-slate-400 hover:text-white hover:bg-slate-800'
    }`}
  >
    <Icon size={18} className="shrink-0" />
    <span className="truncate">{label}</span>
  </button>
);

export default Sidebar;